import LineGradient from "../components/LineGradient";
import useMediaQuery from "../hooks/useMediaQuery";
import { motion } from "framer-motion";
import reactIcon from "../assets/react.png";
import nextJsIcon from "../assets/nextjs.png";
import tailwindIcon from "../assets/tailwind.png";
import pythonIcon from "../assets/python.png";
import typescriptIcon from "../assets/typescript.png";
import herokuIcon from "../assets/heroku.png";
import firebaseIcon from "../assets/firebase.png";
import amazonawsIcon from "../assets/amazonaws.png";
import djangoIcon from "../assets/django.png";

const MySkills = () => {
  const isAboveLarge = useMediaQuery("(min-width: 1060px)");
  return (
    <section id="skills" className="pt-10 pb-24">
      {/* HEADER AND IMAGE SECTION */}
      <div className="md:flex md:justify-between md:gap-16 mt-32">
        <motion.div
          className="md:w-1/3"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, x: -50 },
            visible: { opacity: 1, x: 0 },
          }}
        >
          <p className="font-playfair font-semibold text-4xl mb-5">
            MY <span className="text-red">SKILLS</span>
          </p>
          <LineGradient width="w-1/3" />
          <p className="mt-10 mb-7">
            As a passionate web developer, I’ve honed a diverse set of skills, continuously learning and adapting to new technologies.
          </p>
        </motion.div>

        <div className="mt-16 md:mt-0">
          {isAboveLarge ? (
            <div
              className="relative z-0 ml-20 before:absolute before:-top-10 before:-left-10
              before:w-full before:h-full before:border-2 before:border-blue before:z-[-1]"
            >
              <img
                alt="skills"
                className="z-10"
                src="assets/skills-image.png"
              />
            </div>
          ) : (
            <img alt="skills" className="z-10" src="assets/skills-image.png" />
          )}
        </div>
      </div>

      {/* SKILLS */}
      <div className="md:flex md:justify-between mt-16 gap-32">
        {/* EXPERIENCE */}
        <motion.div
          className="md:w-1/3 mt-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          <div className="relative h-32">
            <div className="z-10">
              <p className="font-playfair font-semibold text-5xl">01</p>
              <p className="font-playfair font-semibold text-3xl mt-3">
                Experience
              </p>
            </div>
            <div className="w-1/2 md:w-3/4 h-32 bg-blue absolute right-0 top-0 z-[-1]" />
          </div>
          <p className="mt-5">
            I have extensive experience working with HTML5, CSS3, Python and JavaScript to create responsive, dynamic web pages.
            I'm also well-versed in modern JavaScript frameworks such as React.
            My background includes building scalable, user-centric applications while paying close attention to performance optimization and cross-browser compatibility.
          </p>
        </motion.div>

        {/* INNOVATIVE */}
        <motion.div
          className="md:w-1/3 mt-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          <div className="relative h-32">
            <div className="z-10">
              <p className="font-playfair font-semibold text-5xl">02</p>
              <p className="font-playfair font-semibold text-3xl mt-3">
                Innovative
              </p>
            </div>
            <div className="w-1/2 md:w-3/4 h-32 bg-red absolute right-0 top-0 z-[-1]" />
          </div>
          <p className="mt-5">
            Innovation drives me to find creative solutions. I’ve used Node.js and Express.js for backend development, building APIs and integrating third-party services.
            I embrace RESTful architecture and am familiar with GraphQL for more efficient data fetching.
            My goal is to craft cutting-edge, performance-driven web applications that provide exceptional user experiences.
          </p>
        </motion.div>
        {/* IMAGINATIVE */}
        <motion.div
          className="md:w-1/3 mt-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          <div className="relative h-32">
            <div className="z-10">
              <p className="font-playfair font-semibold text-5xl">03</p>
              <p className="font-playfair font-semibold text-3xl mt-3">
                Imaginative
              </p>
            </div>
            <div className="w-1/2 md:w-3/4 h-32 bg-yellow absolute right-0 top-0 z-[-1]" />
          </div>
          <p className="mt-5">
            I have a flair for designing clean, intuitive, and visually engaging user interfaces using React.js, CSS Grid and Flexbox.
            With my experience in UI/UX design principles, I ensure that every project I work on is not only functional but also aesthetically pleasing.
            I strive to create interfaces that are not only user-friendly but also forward-thinking, using tools like Figma to prototype and design seamlessly.
          </p>
        </motion.div>
      </div>

   {/* TECH STACK SECTION */}
      <div className="mt-16">
        <motion.div
          className="md:w-full"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
          variants={{
            hidden: { opacity: 0, y: 50 },
            visible: { opacity: 1, y: 0 },
          }}
        >
          <p className="font-playfair font-semibold text-4xl mb-5 text-center">
            <span className="text-red">TECH</span>STACK
          </p>
          <div className="flex justify-center mt-5">
          <LineGradient width="w-1/3" />
          </div>
          <div className="flex flex-wrap justify-center gap-10 mt-10">
            {/* Add each tech stack icon here */}
            <img src={reactIcon} alt="React" className="w-12 h-12" />
            <img src={nextJsIcon} alt="Next.js"className="w-12 h-12" />
            <img src={tailwindIcon} alt="Tailwind" className="w-12 h-12"/>
            <img src={pythonIcon} alt="Python" className="w-12 h-12"/>
            <img src={typescriptIcon} alt="Typescript" className="w-12 h-12"/>
            <img src={herokuIcon} alt="Heroku" className="w-12 h-12"/>
            <img src={firebaseIcon} alt="Firebase" className="w-12 h-12"/>
            <img src={amazonawsIcon} alt="amazonaws" className="w-12 h-12"/>
            <img src={djangoIcon} alt="Django" className="w-12 h-12"/>
          </div>
        </motion.div>
      </div>





    </section>
  );
};

export default MySkills;
